import { useNavigate } from "react-router-dom";
import { NuplayWordmark } from "./components/NuplayWordmark";

// Victoria's Secret — bespoke landing, BRIGHT edition. Light/blush canvas blending VS
// pink (#ED1C6F) with NuPlay blue (#2582EB), high-contrast serif display. "Start the
// demo" goes to the CUSTOMER PICKER (the cards), not straight into a call.

const PICKER = "/nurix-experience/experience?agent=victorias-secret";
const call = (persona: string, query: string) =>
  `/nurix-experience/experience?agent=victorias-secret&persona=${persona}&query=${query}&channel=web`;

const SERIF = "'Didot','Bodoni 72','Bodoni MT','Playfair Display',Georgia,'Times New Roman',serif";
const PINK = "#ED1C6F";
const BLUE = "#2582EB";

const SCENARIOS = [
  { name: "Maya Chen", tier: "Gold", line: "Partial delivery → store warm-transfer", persona: "vs_CUST-0001", query: "wismo", flag: "Flagship" },
  { name: "Emma Patel", tier: "Gold", line: "Defective item → assisted return + offer", persona: "vs_CUST-0003", query: "refund-status" },
  { name: "Olivia Brooks", tier: "Silver", line: "Gifting concierge, end to end", persona: "vs_CUST-0002", query: "product-rec" },
  { name: "Sophia Ramirez", tier: "Gold", line: "Delivered, not received → escalation", persona: "vs_CUST-0004", query: "wismo" },
  { name: "Chloe Williams", tier: "Core", line: "Cancellation denied (the guardrail)", persona: "vs_CUST-0005", query: "cancellation" },
];

const FEATURES = [
  {
    title: "Two steps before a word",
    desc: "Every call is anonymous — no caller ID. Ava identifies you, then verifies with a second factor before anything about the account. She never reads your value back.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={PINK} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
    ),
  },
  {
    title: "Recovery that checks reality",
    desc: "A delayed order gets pickup, overnight or a substitute — and if you pick up, she puts a store associate on the line to confirm the last unit is really on the shelf.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="6" width="15" height="11" rx="1" /><path d="M16 9h4l3 3v5h-7z" /><circle cx="6" cy="19" r="2" /><circle cx="19" cy="19" r="2" /></svg>
    ),
  },
  {
    title: "Honest guardrails, live from the data",
    desc: "It can't cancel an order that's already picking, and it won't fake a refund. When the database says no, Ava says no — warmly — and gives you the path that works.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={PINK} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
    ),
  },
];

function VSWordmark({ size = 15, color = "#17171c" }: { size?: number; color?: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", fontFamily: SERIF, fontWeight: 500, letterSpacing: size * 0.18, fontSize: size, color, textTransform: "uppercase", whiteSpace: "nowrap" }}>
      Victoria's&nbsp;Secret
    </span>
  );
}

export default function VictoriasSecretLandingPage() {
  const navigate = useNavigate();
  return (
    <div className="vs-land">
      <style>{`
        .vs-land{min-height:100vh;background:#fdfbfc;color:#17171c;position:relative;overflow:hidden;font-family:Nunito,system-ui,sans-serif}
        .vs-land *{box-sizing:border-box}
        .vs-glow{position:fixed;inset:0;pointer-events:none;z-index:0}
        .vs-glow::before{content:"";position:absolute;width:820px;height:820px;top:-24%;right:-14%;background:radial-gradient(circle,rgba(237,28,111,.14)0%,transparent 66%);animation:vsfloat 16s ease-in-out infinite}
        .vs-glow::after{content:"";position:absolute;width:640px;height:640px;bottom:-14%;left:-10%;background:radial-gradient(circle,rgba(37,130,235,.12)0%,transparent 70%)}
        @keyframes vsfloat{0%,100%{transform:translateY(0)}50%{transform:translateY(28px)}}
        .vs-wrap{position:relative;z-index:1;max-width:1120px;margin:0 auto;padding:0 28px}
        .vs-nav{display:flex;align-items:center;justify-content:space-between;height:72px;border-bottom:1px solid rgba(20,20,30,.08)}
        .vs-mono{font-family:${SERIF};font-weight:500;font-size:22px;letter-spacing:2px;color:${PINK};border:1.5px solid ${PINK};border-radius:8px;padding:2px 9px;line-height:1}
        .vs-pill{display:inline-flex;align-items:center;gap:7px;font-size:12px;font-weight:600;color:#0f766e;background:rgba(16,185,129,.10);border:1px solid rgba(16,185,129,.28);border-radius:999px;padding:5px 12px}
        .vs-dot{width:6px;height:6px;border-radius:50%;background:#10b981;animation:vspulse 2s infinite}
        @keyframes vspulse{0%{box-shadow:0 0 0 0 rgba(16,185,129,.5)}70%{box-shadow:0 0 0 7px rgba(16,185,129,0)}100%{box-shadow:0 0 0 0 rgba(16,185,129,0)}}
        .vs-hero{padding:76px 0 40px;max-width:840px}
        .vs-eyebrow{font-size:11px;letter-spacing:3px;text-transform:uppercase;color:${PINK};font-weight:800;margin-bottom:22px}
        .vs-eyebrow b{color:${BLUE}}
        .vs-h1{font-family:${SERIF};font-weight:500;font-size:clamp(42px,6.4vw,74px);line-height:1.03;letter-spacing:-.5px;color:#17171c;margin:0 0 22px}
        .vs-h1 em{font-style:italic;background:linear-gradient(100deg,${PINK} 0%,#c81e8a 55%,${BLUE} 130%);-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent}
        .vs-sub{font-size:17px;line-height:1.7;color:#5b5560;max-width:600px;margin:0 0 34px}
        .vs-sub b{color:#17171c;font-weight:700}
        .vs-cta-row{display:flex;flex-wrap:wrap;gap:14px;align-items:center}
        .vs-btn{cursor:pointer;border:none;display:inline-flex;align-items:center;gap:10px;font-size:15px;font-weight:700;padding:15px 26px;border-radius:999px;transition:.15s;font-family:inherit}
        .vs-btn-primary{background:linear-gradient(120deg,${PINK} 0%,#d81b83 45%,${BLUE} 125%);color:#fff;box-shadow:0 10px 30px rgba(237,28,111,.28)}
        .vs-btn-primary:hover{filter:brightness(1.06);transform:translateY(-1px)}
        .vs-btn-ghost{background:#fff;color:#17171c;border:1px solid rgba(20,20,30,.18)}
        .vs-btn-ghost:hover{border-color:${BLUE};color:${BLUE}}
        .vs-meta{margin-top:20px;font-size:13px;color:#8a848f;letter-spacing:.3px}
        .vs-meta b{color:${PINK};font-weight:800}
        .vs-section-label{font-size:11px;letter-spacing:3px;text-transform:uppercase;color:#8a848f;font-weight:800;margin:0 0 18px}
        .vs-scen{display:grid;grid-template-columns:repeat(auto-fill,minmax(232px,1fr));gap:14px;padding-bottom:56px}
        .vs-card{cursor:pointer;text-align:left;background:#fff;border:1px solid rgba(20,20,30,.09);border-radius:14px;padding:20px 20px 18px;transition:.15s;position:relative;overflow:hidden;box-shadow:0 1px 2px rgba(20,20,30,.04)}
        .vs-card:hover{border-color:rgba(237,28,111,.45);transform:translateY(-2px);box-shadow:0 14px 34px rgba(237,28,111,.14)}
        .vs-card .nm{font-family:${SERIF};font-weight:500;font-size:20px;color:#17171c;margin-bottom:2px}
        .vs-card .tier{font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:${PINK};font-weight:800}
        .vs-card .ln{font-size:13px;color:#5b5560;line-height:1.5;margin-top:10px}
        .vs-card .flag{position:absolute;top:14px;right:14px;font-size:9px;letter-spacing:1px;text-transform:uppercase;color:#fff;background:${PINK};font-weight:800;border-radius:5px;padding:3px 7px}
        .vs-card .go{margin-top:14px;font-size:12px;font-weight:700;color:${BLUE};display:inline-flex;align-items:center;gap:6px}
        .vs-feats{display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:28px;padding:44px 0 60px;border-top:1px solid rgba(20,20,30,.08)}
        .vs-feat .ic{width:44px;height:44px;border-radius:12px;border:1px solid rgba(237,28,111,.22);background:linear-gradient(135deg,rgba(237,28,111,.07),rgba(37,130,235,.07));display:flex;align-items:center;justify-content:center;margin-bottom:16px}
        .vs-feat .ft{font-size:16px;font-weight:800;color:#17171c;margin-bottom:8px}
        .vs-feat .fd{font-size:14px;line-height:1.65;color:#5b5560}
        .vs-foot{border-top:1px solid rgba(20,20,30,.08);padding:26px 0;display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap}
        .vs-foot .pow{display:inline-flex;align-items:center;gap:8px;font-size:13px;color:#8a848f}
      `}</style>
      <div className="vs-glow" />

      {/* Nav */}
      <div className="vs-wrap">
        <nav className="vs-nav">
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <span className="vs-mono">VS</span>
            <VSWordmark size={15} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
            <span className="pow" style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "#8a848f" }}>
              Powered by <span style={{ color: "#17171c", display: "inline-flex" }}><NuplayWordmark height={15} /></span>
            </span>
            <button className="vs-pill" style={{ cursor: "pointer" }} onClick={() => navigate(PICKER)}>
              <span className="vs-dot" /> Ava online
            </button>
          </div>
        </nav>
      </div>

      {/* Hero */}
      <div className="vs-wrap">
        <div className="vs-hero">
          <div className="vs-eyebrow">Ava · Customer Care · <b>Powered by NuPlay</b></div>
          <h1 className="vs-h1">It calls the store to check <em>the last one.</em></h1>
          <p className="vs-sub">
            Meet <b>Ava</b>, Victoria's Secret's care crew across six desks — orders and recovery, returns and care, gifting and escalations.
            When the last pyjama set is "showing one left," she doesn't send you on a system count — she warm-transfers the store to physically check it first.
          </p>
          <div className="vs-cta-row">
            <button className="vs-btn vs-btn-primary" onClick={() => navigate(PICKER)}>
              Browse customers
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <button className="vs-btn vs-btn-ghost" onClick={() => navigate(call("vs_CUST-0001", "wismo"))}>
              Run the flagship call
            </button>
          </div>
          <div className="vs-meta"><b>5</b> scripted scenarios · <b>25</b> live customers · no account needed</div>
        </div>
      </div>

      {/* Scenario cards */}
      <div className="vs-wrap">
        <p className="vs-section-label">Pick a customer to call as</p>
        <div className="vs-scen">
          {SCENARIOS.map((s) => (
            <button key={s.persona} className="vs-card" onClick={() => navigate(call(s.persona, s.query))}>
              {s.flag && <span className="flag">{s.flag}</span>}
              <div className="tier">{s.tier}</div>
              <div className="nm">{s.name}</div>
              <div className="ln">{s.line}</div>
              <div className="go">Start this call
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Features */}
      <div className="vs-wrap">
        <div className="vs-feats">
          {FEATURES.map((f) => (
            <div className="vs-feat" key={f.title}>
              <div className="ic">{f.icon}</div>
              <div className="ft">{f.title}</div>
              <div className="fd">{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="vs-wrap">
        <div className="vs-foot">
          <VSWordmark size={13} color="#8a848f" />
          <span className="pow">Powered by <span style={{ color: "#17171c", display: "inline-flex" }}><NuplayWordmark height={15} /></span></span>
        </div>
      </div>
    </div>
  );
}
